import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Scissors, User, Package, AlertTriangle } from "lucide-react";

type UsageType = "professional_own" | "internal_service";

interface RegisterUsageModalProps {
  /** Se fornecido, o profissional fica fixo (não pode ser alterado) */
  fixedProfessionalId?: number;
  fixedProfessionalName?: string;
  /** Se fornecido, o produto fica fixo (não pode ser alterado) */
  fixedProductId?: number;
  fixedProductName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const fmtCurrency = (v: string | number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    parseFloat(String(v))
  );

export function RegisterUsageModal({
  fixedProfessionalId,
  fixedProfessionalName,
  fixedProductId,
  fixedProductName,
  onClose,
  onSuccess,
}: RegisterUsageModalProps) {
  const [professionalId, setProfessionalId] = useState<string>(
    fixedProfessionalId ? String(fixedProfessionalId) : ""
  );
  const [productId, setProductId] = useState<string>(
    fixedProductId ? String(fixedProductId) : ""
  );
  const [qty, setQty] = useState("1");
  const [usageType, setUsageType] = useState<UsageType>("internal_service");
  const [serviceId, setServiceId] = useState("");
  const [notes, setNotes] = useState("");

  const utils = trpc.useUtils();

  // Buscar profissionais
  const { data: professionals = [] } = trpc.professionals.listAdmin.useQuery();
  const { data: services = [] } = trpc.services.listAdmin.useQuery();

  // Buscar produtos que têm deductFromCommission = true
  const productsInput = useMemo(() => ({ search: "", category: "" }), []);
  const { data: allProducts = [] } = trpc.products.list.useQuery(productsInput);
  const deductibleProducts = allProducts.filter((p) => p.deductFromCommission);

  // Produto selecionado para mostrar os custos
  const selectedProduct = deductibleProducts.find((p) => p.id === Number(productId));
  const selectedProfessional = professionals.find((p) => p.id === Number(professionalId));

  // Calcular custo unitário conforme tipo de uso
  const unitCost = selectedProduct
    ? usageType === "professional_own"
      ? parseFloat(selectedProduct.professionalUseCost ?? selectedProduct.costPrice)
      : parseFloat(selectedProduct.internalUseCost ?? selectedProduct.costPrice)
    : 0;
  const totalDeducted = unitCost * (parseFloat(qty) || 0);

  const registerUsage = trpc.products.registerUsage.useMutation({
    onSuccess: (data) => {
      toast.success(
        `Uso registrado! Desconto de ${fmtCurrency(data.totalDeducted)} na comissão de ${selectedProfessional?.name ?? "profissional"}.`
      );
      utils.products.list.invalidate();
      utils.products.getMovements.invalidate();
      utils.products.usageReport.invalidate();
      onSuccess?.();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!professionalId || !productId) {
      toast.error("Selecione o profissional e o produto.");
      return;
    }
    if (usageType === "internal_service" && !serviceId) {
      toast.error("Selecione o serviço em que o produto foi utilizado.");
      return;
    }
    const qtyNum = parseFloat(qty);
    if (!qtyNum || qtyNum <= 0) {
      toast.error("Informe uma quantidade válida.");
      return;
    }
    registerUsage.mutate({
      productId: Number(productId),
      professionalId: Number(professionalId),
      qty: qtyNum,
      usageType,
      serviceId: usageType === "internal_service" ? Number(serviceId) : undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />
            Registrar Uso de Produto
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            O valor será descontado automaticamente da comissão do profissional.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profissional */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              Profissional
            </Label>
            {fixedProfessionalId ? (
              <div className="h-9 px-3 rounded-md border bg-muted/40 flex items-center text-sm">
                {fixedProfessionalName ?? `Profissional #${fixedProfessionalId}`}
              </div>
            ) : (
              <Select value={professionalId} onValueChange={setProfessionalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o profissional..." />
                </SelectTrigger>
                <SelectContent>
                  {professionals.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Produto */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Produto
            </Label>
            {fixedProductId ? (
              <div className="h-9 px-3 rounded-md border bg-muted/40 flex items-center text-sm">
                {fixedProductName ?? `Produto #${fixedProductId}`}
              </div>
            ) : (
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o produto..." />
                </SelectTrigger>
                <SelectContent>
                  {deductibleProducts.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Nenhum produto configurado para desconto de comissão
                    </div>
                  ) : (
                    deductibleProducts.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        <span className="flex items-center gap-2">
                          {p.name}
                          {p.isInternalUse && (
                            <Badge variant="secondary" className="text-xs py-0">
                              Interno
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Tipo de uso */}
          <div className="space-y-1.5">
            <Label>Tipo de Uso</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setUsageType("internal_service")}
                className={`rounded-lg border p-3 text-left transition-all ${
                  usageType === "internal_service"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <Scissors className="h-4 w-4 mb-1" />
                <p className="text-xs font-semibold">Uso Interno</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Usado no serviço do cliente
                </p>
                {selectedProduct && (
                  <p className="text-xs font-bold mt-1 text-primary">
                    {fmtCurrency(selectedProduct.internalUseCost ?? selectedProduct.costPrice)}/un
                  </p>
                )}
              </button>
              <button
                type="button"
                onClick={() => setUsageType("professional_own")}
                className={`rounded-lg border p-3 text-left transition-all ${
                  usageType === "professional_own"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <User className="h-4 w-4 mb-1" />
                <p className="text-xs font-semibold">Uso Próprio</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Profissional usou para si
                </p>
                {selectedProduct && (
                  <p className="text-xs font-bold mt-1 text-primary">
                    {fmtCurrency(selectedProduct.professionalUseCost ?? selectedProduct.costPrice)}/un
                  </p>
                )}
              </button>
            </div>
          </div>

          {usageType === "internal_service" && (
            <div className="space-y-1.5">
              <Label>Serviço realizado</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger><SelectValue placeholder="Selecione o serviço..." /></SelectTrigger>
                <SelectContent>{services.map((service) => <SelectItem key={service.id} value={String(service.id)}>{service.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {/* Quantidade */}
          <div className="space-y-1.5">
            <Label>Quantidade {selectedProduct ? `(${selectedProduct.unit ?? "un"})` : ""}</Label>
            <Input
              type="number"
              step="0.001"
              min="0.001"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              required
            />
          </div>

          {/* Resumo do desconto */}
          {selectedProduct && professionalId && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-600 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Resumo do Desconto
              </p>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Custo unitário ({usageType === "professional_own" ? "uso próprio" : "uso interno"}):</span>
                <span className="font-medium">{fmtCurrency(unitCost)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Quantidade:</span>
                <span className="font-medium">{parseFloat(qty) || 0} {selectedProduct.unit ?? "un"}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t border-amber-500/20 pt-1 mt-1">
                <span>Total a descontar da comissão:</span>
                <span className="text-amber-600">{fmtCurrency(totalDeducted)}</span>
              </div>
            </div>
          )}

          {/* Observações */}
          <div className="space-y-1.5">
            <Label>Observações (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ex: Usado na coloração da cliente..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={registerUsage.isPending || !professionalId || !productId || (usageType === "internal_service" && !serviceId)}
            >
              {registerUsage.isPending ? "Registrando..." : "Registrar Uso"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
